import { useState, useEffect, useRef, useCallback } from "react";

interface FullscreenPreviewProps {
  media: { url: string; type: string; title: string };
  originRect: DOMRect | null;
  onClose: () => void;
  onDownload: () => void;
}

export default function FullscreenPreview({ media, originRect, onClose, onDownload }: FullscreenPreviewProps) {
  const [phase, setPhase] = useState<"enter" | "active" | "exit">("enter");
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isVideo = media.type === "video";

  // Calculate initial transform based on originRect
  const getInitialStyle = (): React.CSSProperties => {
    if (!originRect) return {};
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const targetW = vw * 0.9;
    const targetH = vh * 0.85;
    const targetX = (vw - targetW) / 2;
    const targetY = (vh - targetH) / 2;

    const scaleX = originRect.width / targetW;
    const scaleY = originRect.height / targetH;
    const scale = Math.min(scaleX, scaleY);

    const originCenterX = originRect.left + originRect.width / 2;
    const originCenterY = originRect.top + originRect.height / 2;
    const targetCenterX = targetX + targetW / 2;
    const targetCenterY = targetY + targetH / 2;

    const translateX = originCenterX - targetCenterX;
    const translateY = originCenterY - targetCenterY;

    return {
      transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
      transformOrigin: "center center",
      opacity: 0.6,
    };
  };

  // After mount, transition to fullscreen
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPhase("active");
      });
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Handle close with reverse animation
  const handleClose = useCallback(() => {
    setPhase("exit");
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  // Keyboard: Escape to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleClose]);

  // Lock body scroll
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, []);

  // Video controls auto-hide logic
  const resetControlsTimer = useCallback(() => {
    setControlsVisible(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if (!isVideo) return;
    resetControlsTimer();
    return () => { if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current); };
  }, [isVideo, resetControlsTimer]);

  const handleMediaInteraction = useCallback(() => {
    if (!isVideo) return;
    resetControlsTimer();
  }, [isVideo, resetControlsTimer]);

  const [paused, setPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlayPause = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPaused(false); }
    else { v.pause(); setPaused(true); }
    resetControlsTimer();
  }, [resetControlsTimer]);

  const getTransform = () => {
    if (phase === "exit") return getInitialStyle();
    return { transform: "translate(0, 0) scale(1)", opacity: 1 };
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      onMouseMove={handleMediaInteraction}
      onTouchStart={handleMediaInteraction}
      style={{ background: phase === "active" ? "rgba(0,0,0,0.92)" : "rgba(0,0,0,0)", transition: "background 0.35s ease" }}
    >
      {/* Media container with hero transition */}
      <div
        className="relative flex items-center justify-center overflow-hidden rounded-lg"
        style={{
          width: "90vw",
          height: "85vh",
          maxWidth: "90vw",
          maxHeight: "85vh",
          transition: phase === "active" ? "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease" : "transform 0.28s cubic-bezier(0.4, 0, 1, 1), opacity 0.25s ease",
          ...getTransform(),
        }}
      >
        {isVideo ? (
          <video
            ref={videoRef}
            src={media.url}
            className="w-full h-full object-contain rounded-lg"
            autoPlay
            playsInline
            preload="metadata"
            onClick={togglePlayPause}
            onPlay={() => setPaused(false)}
            onPause={() => setPaused(true)}
          >
            您的浏览器不支持视频播放
          </video>
        ) : (
          <img
            src={media.url}
            alt={media.title}
            className="w-full h-full object-contain rounded-lg"
            onError={(e) => { (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%239ca3af' font-size='12'%3E加载失败%3C/text%3E%3C/svg%3E"; }}
          />
        )}

        {/* Video controls overlay */}
        {isVideo && (
          <div
            className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pb-4 pt-12 transition-opacity duration-300 ${controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={togglePlayPause}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white cursor-pointer transition-colors"
              >
                <i className={`${paused ? "ri-play-fill" : "ri-pause-fill"} text-lg`}></i>
              </button>
              <div className="flex-1"></div>
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Close button (non-video mode, always visible top-right) */}
      {!isVideo && (
        <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white cursor-pointer transition-colors"
            title="下载"
          >
            <i className="ri-download-line text-lg"></i>
          </button>
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      )}

      {/* Video top bar */}
      {isVideo && (
        <div
          className={`absolute top-0 inset-x-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent px-4 pt-4 pb-12 transition-opacity duration-300 flex items-center justify-between ${controlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <p className="text-sm text-white/90 truncate max-w-[60vw]">{media.title}</p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(); }}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white cursor-pointer transition-colors"
              title="下载"
            >
              <i className="ri-download-line"></i>
            </button>
            <button
              onClick={handleClose}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>
      )}

      {/* Paused overlay for videos */}
      {isVideo && paused && (
        <div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={togglePlayPause}
        >
          <div className="w-16 h-16 rounded-full bg-white/25 flex items-center justify-center backdrop-blur-sm">
            <i className="ri-play-fill text-white text-3xl ml-1"></i>
          </div>
        </div>
      )}
    </div>
  );
}